import React from 'react'
import { IStage } from '@forwardflow/store'
import { useDroppable } from '@dnd-kit/core'
import { DnDStageAndTagPill } from './DnDStageAndTagPill'
import cn from 'classnames'
import { getBgColorByIdentifier, hexToRgba } from '../utils'
import { TStageGroupIdentifier } from '../interfaces/stage-mapping.types'
import { useDnDStageMappingContext } from '../hooks/useDnDStageMappingContext'
import { TrashIcon } from '@forwardflow/icons'

interface DnDStageGroupDropZoneProps {
   id: TStageGroupIdentifier | string // Allow string for timeline-based IDs
   key: TStageGroupIdentifier | string
   stages?: IStage[]
   tags?: IStage[]
   onClose?: (stageId: string) => void
   onCloseTag?: (tagId: string) => void
   title?: string
   icon?: React.ReactNode
   description?: string
   isMandatory?: boolean
   timeline?: string // For nurturing groups
   groupId?: TStageGroupIdentifier // The actual group ID when using timeline
   onDelete?: () => void // For deleting nurturing groups
   onEdit?: () => void // For editing nurturing groups
}

export const DnDStageGroupDropZone = ({
   id,
   stages = [],
   tags = [],
   onClose,
   onCloseTag,
   title = 'title',
   icon,
   description,
   isMandatory = false,
   timeline,
   groupId,
   onDelete,
   onEdit,
}: DnDStageGroupDropZoneProps) => {
   const { setNodeRef, isOver } = useDroppable({
      id,
   })

   const { removeStageFromGroup, removeTagFromGroup } =
      useDnDStageMappingContext()

   const bgColor = getBgColorByIdentifier(
      groupId || (id as TStageGroupIdentifier)
   )

   const filteredStages = stages.filter((s) => s.id && s.name?.trim())
   const filteredTags = tags.filter((t) => t.id && t.name?.trim())

   const handleStageClose = (stageId: string) => {
      // Use groupId if provided (for nurturing), otherwise use id
      const dropZoneIdentifier = (groupId || id) as TStageGroupIdentifier
      removeStageFromGroup(stageId, dropZoneIdentifier, timeline)
      onClose?.(stageId)
   }

   const handleTagClose = (tagId: string) => {
      // Use groupId if provided (for nurturing), otherwise use id
      const dropZoneIdentifier = (groupId || id) as TStageGroupIdentifier
      removeTagFromGroup(tagId, dropZoneIdentifier, timeline)
      onCloseTag?.(tagId)
   }

   return (
      <div
         ref={setNodeRef}
         className={cn(
            'mt-6 rounded-xl border border-white/20 transition-colors',
            {
               'opacity-20': isOver,
            }
         )}
         style={{
            backgroundColor: hexToRgba(bgColor || '#9F7BFF', 0.4),
         }}
      >
         <div className="bg-white/[0.04] rounded-xl border border-white/10">
            <div className="rounded-xl border border-white/10 bg-white/[0.04] px-8 py-6 ">
               <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-white text-2xl font-medium">
                     {icon}
                     <p>{title}</p>
                  </div>
                  <div className="flex items-center gap-2">
                     {isMandatory && (
                        <p className="rounded-[8px] bg-white/20 text-white px-4 py-1 text-sm">
                           Mandatory
                        </p>
                     )}
                     {onEdit && (
                        <button
                           onClick={onEdit}
                           className="bg-transparent hover:opacity-70 transition-opacity"
                           type="button"
                        >
                           <svg
                              width={20}
                              height={20}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                           >
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                           </svg>
                        </button>
                     )}
                     {onDelete && (
                        <button
                           onClick={onDelete}
                           className="bg-transparent hover:opacity-70 transition-opacity"
                           type="button"
                        >
                           <TrashIcon width={24} height={24} />
                        </button>
                     )}
                  </div>
               </div>

               {description && (
                  <p className="text-sm text-white/70 mt-3">{description}</p>
               )}
            </div>
            {filteredStages.length > 0 && (
               <div className="px-8 py-4 grid grid-cols-[80px_auto] gap-2.5">
                  <p className="w-14 pt-1 font-medium">Stage</p>
                  {filteredStages.length > 0 && (
                     <div className="flex flex-wrap gap-2.5">
                        {filteredStages.map((stage) => (
                           <DnDStageAndTagPill
                              key={stage.id}
                              stage={stage}
                              onClose={handleStageClose}
                              isSelected={true}
                           />
                        ))}
                     </div>
                  )}
               </div>
            )}
         </div>
         {filteredTags.length > 0 && (
            <div className="px-8 py-4 grid grid-cols-[80px_auto] gap-2.5">
               <p className="w-14 font-medium">Tag</p>

               <div className="flex flex-wrap gap-2.5">
                  {filteredTags.map((tag) => (
                     <DnDStageAndTagPill
                        key={tag.id}
                        stage={tag}
                        onClose={handleTagClose}
                        isSelected={true}
                     />
                  ))}
               </div>
            </div>
         )}
         {filteredTags.length === 0 && filteredStages.length === 0 && (
            <div className="px-8 py-4 flex items-center font-medium h-full">
               <p className="text-white/80">
                  Drag and drop stages or tags here
               </p>
            </div>
         )}
      </div>
   )
}
